import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, Search, Crown, Shield, Trash2, Edit, CheckCircle2, XCircle, Loader2, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', company: '', plan: 'free', is_admin: false });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const me = JSON.parse(localStorage.getItem('user') || '{}');
  const [aiUsage, setAiUsage] = useState({});
  const [aiSummary, setAiSummary] = useState(null);

  useEffect(() => {
    if (!me.is_admin) return;
    loadUsers();
    loadAiUsage();
  }, []);

  const loadAiUsage = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/ai/usage?user_id=${me.id}`);
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);
      const map = {};
      list.forEach(u => { map[u.user_id] = u; });
      setAiUsage(map);
      setAiSummary(data.summary || null);
    } catch (e) {}
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users?user_id=${me.id}`);
      if (!res.ok) throw new Error();
      setUsers(await res.json());
    } catch (e) { toast.error('Erreur ou accès refusé'); }
    finally { setLoading(false); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      company: u.company || '',
      plan: u.plan || 'free',
      is_admin: !!u.is_admin,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: me.id, ...editForm }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Utilisateur mis à jour');
      setEditing(null);
      loadUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${deleteConfirm.id}?admin_id=${me.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Utilisateur supprimé');
      setDeleteConfirm(null);
      loadUsers();
    } catch (e) { toast.error('Erreur'); }
  };

  if (!me.is_admin) {
    return <div className="p-8 text-center text-muted-foreground">Accès admin requis.</div>;
  }

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q) || u.company?.toLowerCase().includes(q);
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold mb-2 tracking-tight flex items-center gap-3">
          <Users size={32} className="text-[#205F79]" />
          Admin — Utilisateurs
        </h1>
        <p className="text-muted-foreground">Gestion des comptes utilisateurs (administratif uniquement)</p>
      </div>

      {/* Privacy banner */}
      <Card className="mb-6 border-amber-200 bg-amber-50">
        <CardContent className="pt-5">
          <div className="flex items-start gap-3">
            <Lock className="text-amber-700 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Confidentialité des données métier</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                Conformément à la Politique de confidentialité, les administrateurs n'ont <strong>aucun accès</strong>
                aux formules, recettes, DIP, fiches matières premières, étiquettes ou rapports toxicologiques des
                utilisateurs. Cette interface ne permet que la gestion administrative des comptes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI usage summary */}
      {aiSummary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="ai-summary-cards">
            <div className="p-4 rounded-lg border border-gray-200 bg-white">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Conso IA ce mois</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{aiSummary.total_used_eur.toFixed(2)} €</p>
              <p className="text-[10px] text-muted-foreground mt-1">Mois {aiSummary.month}</p>
            </div>
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
              <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Users {'>'}50%</p>
              <p className="text-2xl font-bold text-amber-900 mt-1">{aiSummary.users_over_50}</p>
              <p className="text-[10px] text-amber-700 mt-1">Surveillance recommandée</p>
            </div>
            <div className="p-4 rounded-lg border border-red-200 bg-red-50">
              <p className="text-xs uppercase tracking-wider text-red-700 font-semibold">Alertes 90%+</p>
              <p className="text-2xl font-bold text-red-900 mt-1">{aiSummary.users_over_90}</p>
              <p className="text-[10px] text-red-700 mt-1">Risque blocage imminent</p>
            </div>
            <div className="p-4 rounded-lg border border-green-200 bg-green-50">
              <p className="text-xs uppercase tracking-wider text-green-700 font-semibold">Revenue Boost IA</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{aiSummary.boost_revenue_eur.toFixed(2)} €</p>
              <p className="text-[10px] text-green-700 mt-1">{aiSummary.boosts_paid_this_month} boost(s) vendu(s)</p>
            </div>
          </div>
          
          {/* Quotas IA info */}
          <div className="mb-6 p-3 rounded-lg border border-blue-200 bg-blue-50">
            <p className="text-xs font-semibold text-blue-900 mb-1">💡 Quotas IA mensuels</p>
            <div className="flex gap-6 text-xs text-blue-800">
              <span><strong>Gratuit:</strong> 0,05€ (~0,25 crédit)</span>
              <span><strong>Premium:</strong> 3,00€ (~15 crédits)</span>
              <span className="opacity-70">• 1 crédit Emergent = 0,20€</span>
            </div>
          </div>
        </>
      )}

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Rechercher email, nom, société..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-users-search" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Comptes ({filtered.length})</CardTitle>
          <CardDescription>Date d'inscription, acceptation des CGU, plan et stats produits/matières.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="skeleton-shimmer h-32 rounded-lg" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="pb-3 pr-3">Utilisateur</th>
                    <th className="pb-3 pr-3">Email</th>
                    <th className="pb-3 pr-3">Plan</th>
                    <th className="pb-3 pr-3 text-center">Produits</th>
                    <th className="pb-3 pr-3">IA ce mois</th>
                    <th className="pb-3 pr-3">Inscrit</th>
                    <th className="pb-3 pr-3">CGU acceptées</th>
                    <th className="pb-3 pr-3">Admin</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-b border-border/50 hover:bg-muted/40" data-testid={`admin-user-row-${u.id}`}>
                      <td className="py-3 pr-3">
                        <p className="font-medium">{u.name || '—'}</p>
                        {u.company && <p className="text-xs text-muted-foreground">{u.company}</p>}
                      </td>
                      <td className="py-3 pr-3 text-muted-foreground">{u.email}</td>
                      <td className="py-3 pr-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${u.plan === 'premium' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                          {u.plan === 'premium' ? <span className="inline-flex items-center gap-1"><Crown size={10} /> Premium</span> : 'Gratuit'}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-center">{u.product_count}</td>
                      <td className="py-3 pr-3 text-xs whitespace-nowrap">
                        {(() => {
                          const ai = aiUsage[u.id];
                          if (!ai) return <span className="text-muted-foreground">—</span>;
                          const cls = ai.blocked
                            ? 'text-red-700 bg-red-50 border-red-200'
                            : ai.warning_90
                              ? 'text-amber-700 bg-amber-50 border-amber-200'
                              : 'text-gray-700 bg-gray-50 border-gray-200';
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls}`} title={`${ai.used_eur.toFixed(3)}€ / ${(ai.limit_eur + ai.boost_eur).toFixed(2)}€`}>
                              {ai.warning_90 && !ai.blocked && <AlertTriangle size={11} />}
                              {ai.blocked && <XCircle size={11} />}
                              <span className="font-semibold">{ai.pct}%</span>
                              <span className="text-[10px] opacity-70">({ai.used_eur.toFixed(2)}€)</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="py-3 pr-3 text-xs">
                        {u.cgu_accepted_at ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 size={12} /> {new Date(u.cgu_accepted_at).toLocaleDateString('fr-FR')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <XCircle size={12} /> Non
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        {u.is_admin && <Shield size={14} className="text-[#205F79]" />}
                      </td>
                      <td className="py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`} className="gap-1">
                          <Edit size={14} /> Modifier
                        </Button>
                        {u.id !== me.id && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(u)} data-testid={`btn-delete-user-${u.id}`} className="gap-1 text-destructive hover:text-destructive">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Aucun utilisateur trouvé.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nom">
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
            </Field>
            <Field label="Email">
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" />
            </Field>
            <Field label="Société">
              <Input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} data-testid="edit-user-company" />
            </Field>
            <Field label="Plan">
              <select className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={editForm.plan} onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                data-testid="edit-user-plan">
                <option value="free">Gratuit</option>
                <option value="premium">Premium</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editForm.is_admin} onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })} data-testid="edit-user-isadmin" className="w-4 h-4 accent-[#205F79]" />
              <span className="text-sm">Compte administrateur</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={saving} className="gap-2 bg-[#205F79] hover:bg-[#184a5e]" data-testid="btn-save-user-edit">
              {saving && <Loader2 className="animate-spin" size={14} />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-destructive" size={20} />
              Supprimer définitivement ce compte ?
            </DialogTitle>
            <DialogDescription>
              <strong>{deleteConfirm?.email}</strong> et toutes ses données seront supprimés. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete-user">
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</label>
    {children}
  </div>
);

export default AdminUsers;
